/**
 * Live Quiz Player WebSocket Hook
 *
 * Manages WebSocket connection and gameplay for quiz participants.
 */

import { useEffect, useRef, useCallback } from 'react';
import { usePlayerStore } from '../stores/liveQuizStore';
import type { ServerMessage, ClientMessage } from '../lib/liveQuizProtocol';

interface UseLiveQuizPlayerOptions {
  sessionId: string;
  participantId: string;
  onError?: (error: string) => void;
  onKicked?: (reason?: string) => void;
}

export function useLiveQuizPlayer({
  sessionId,
  participantId,
  onError,
  onKicked,
}: UseLiveQuizPlayerOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);

  const {
    connected,
    connecting,
    error,
    sessionId: storeSessionId,
    gamePin,
    status,
    settings,
    participantId: storeParticipantId,
    nickname,
    avatar,
    currentQuestionIndex,
    totalQuestions,
    currentQuestion,
    timeRemaining,
    isReady,
    myAnswer,
    myScore,
    myRank,
    myStreak,
    lastResult,
    participantCount,
    answeredCount,
    correctAnswer,
    leaderboard,
    setConnected,
    setConnecting,
    setError,
    setSession,
    setStatus,
    setQuestion,
    setTimeRemaining,
    setIsReady,
    setMyAnswer,
    setLastResult,
    setParticipantCount,
    setAnsweredCount,
    setCorrectAnswer,
    setLeaderboard,
    reset,
    resetForNextQuestion,
  } = usePlayerStore();

  // Handle incoming messages
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data) as ServerMessage;
      console.log('[Player WS] Received:', message.type, message);

      switch (message.type) {
        case 'welcome':
          if (message.you) {
            setSession(
              message.sessionId,
              message.gamePin,
              message.settings,
              message.totalQuestions,
              message.you.id,
              message.you.nickname,
              message.you.avatar
            );
          }
          setStatus(message.status);
          setParticipantCount(message.participants.length);
          break;

        case 'player_joined':
          setParticipantCount(message.count);
          break;

        case 'player_left':
          setParticipantCount(message.count);
          break;

        case 'game_starting':
          setStatus('starting');
          break;

        case 'question_start':
          setStatus('question');
          resetForNextQuestion();
          setQuestion(
            message.question,
            message.index,
            message.timeSeconds
          );
          break;

        case 'time_update':
          setTimeRemaining(message.remaining);
          break;

        case 'answer_count_update':
          setAnsweredCount(message.answeredCount);
          setParticipantCount(message.totalCount);
          break;

        case 'answer_result':
          setLastResult({
            correct: message.correct,
            points: message.points,
            totalScore: message.totalScore,
            streak: message.streak,
            rank: message.rank,
            responseTimeMs: message.responseTimeMs,
          });
          break;

        case 'question_end':
          setStatus('reveal');
          if (message.correctAnswer !== undefined) {
            setCorrectAnswer(message.correctAnswer);
          }
          break;

        case 'leaderboard':
          setStatus('leaderboard');
          setLeaderboard(message.entries);
          break;

        case 'game_paused':
          setStatus('paused');
          break;

        case 'game_resumed':
          setStatus('question');
          setTimeRemaining(message.timeRemaining);
          break;

        case 'game_ended':
          setStatus('ended');
          setLeaderboard(message.finalLeaderboard);
          break;

        case 'status_change':
          setStatus(message.status);
          break;

        case 'kicked':
          disconnect();
          onKicked?.(message.reason);
          break;

        case 'error':
          setError(message.message);
          onError?.(message.message);
          break;

        case 'pong':
          // Heartbeat response
          break;

        case 'reconnected':
          setStatus(message.sessionState.status);
          setTimeRemaining(message.sessionState.timeRemaining);
          break;
      }
    } catch (error) {
      console.error('Failed to parse message:', error);
    }
  }, [
    setSession,
    setStatus,
    setParticipantCount,
    setQuestion,
    setTimeRemaining,
    setAnsweredCount,
    setLastResult,
    setCorrectAnswer,
    setLeaderboard,
    setError,
    resetForNextQuestion,
    onError,
    onKicked,
  ]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setConnecting(true);
    setError(null);

    // In production, connect directly to the live-quiz-worker
    // In development, use the local proxy
    const isProduction = window.location.hostname.includes('.pages.dev') ||
                         window.location.hostname.includes('.workers.dev') ||
                         !window.location.hostname.includes('localhost');

    let wsUrl: string;
    if (isProduction) {
      // Direct connection to live-quiz-worker in production
      wsUrl = `wss://live-quiz-worker.ziv-ba4.workers.dev/session/${sessionId}/?participantId=${participantId}`;
    } else {
      // Local development proxy
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/api/live-quiz/ws/${sessionId}?participantId=${participantId}`;
    }

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setConnected(true);
      reconnectAttemptsRef.current = 0;
    };

    ws.onclose = (event) => {
      setConnected(false);

      // Attempt reconnection for non-clean closes
      if (!event.wasClean && reconnectAttemptsRef.current < 5) {
        reconnectAttemptsRef.current++;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };

    ws.onerror = () => {
      setError('Connection error');
    };

    ws.onmessage = handleMessage;

    wsRef.current = ws;
  }, [sessionId, participantId, setConnecting, setConnected, setError, handleMessage]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    reset();
  }, [reset]);

  // Send message
  const send = useCallback((message: ClientMessage) => {
    console.log('[Player WS] send() called, readyState:', wsRef.current?.readyState, 'OPEN=', WebSocket.OPEN);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('[Player WS] Sending:', message);
      wsRef.current.send(JSON.stringify(message));
    } else {
      console.log('[Player WS] Cannot send - WebSocket not open');
    }
  }, []);

  // Player actions
  const setReadyAction = useCallback(() => {
    console.log('[Player WS] Sending player:ready');
    send({ type: 'player:ready' });
    setIsReady(true);
  }, [send, setIsReady]);

  const submitAnswer = useCallback((answer: unknown) => {
    if (myAnswer !== null) return; // Already answered

    setMyAnswer(answer);
    send({
      type: 'player:submit_answer',
      answer,
      questionIndex: currentQuestionIndex,
    });
  }, [send, myAnswer, currentQuestionIndex, setMyAnswer]);

  // Heartbeat
  useEffect(() => {
    if (!connected) return;

    const interval = setInterval(() => {
      send({ type: 'ping' });
    }, 30000);

    return () => clearInterval(interval);
  }, [connected, send]);

  // Connect on mount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  // Computed values
  const canAnswer = status === 'question' && myAnswer === null && timeRemaining > 0;
  const isWaiting = status === 'question' && myAnswer !== null;
  const myLeaderboardEntry = leaderboard.find((e) => e.participantId === participantId);

  return {
    // Connection state
    connected,
    connecting,
    error,

    // Session info
    sessionId: storeSessionId,
    gamePin,
    status,
    settings,

    // Player info
    participantId: storeParticipantId,
    nickname,
    avatar,

    // Question state
    currentQuestionIndex,
    totalQuestions,
    currentQuestion,
    timeRemaining,

    // My state
    isReady,
    myAnswer,
    myScore,
    myRank,
    myStreak,
    lastResult,

    // Game state
    participantCount,
    answeredCount,
    correctAnswer,
    leaderboard,
    myLeaderboardEntry,

    // Computed
    canAnswer,
    isWaiting,

    // Actions
    setReady: setReadyAction,
    submitAnswer,
    reconnect: connect,
    disconnect,
  };
}

export default useLiveQuizPlayer;
